import { Component, lazy, Suspense, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Auth from "./pages/Auth";
import { ViewAsModBanner } from "./components/ViewAsModBanner";
import { DonationPopup } from "./components/DonationPopup";
import { ProUpgradePopup } from "./components/ProUpgradePopup";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SaveyLoader } from "./components/SaveyLoader";
import { NoWifiDialog } from "./components/NoWifiDialog";

// Lazy-load non-critical routes to shrink the initial JS bundle and improve LCP.
const Index = lazy(() => import("./pages/Index"));
const Landing = lazy(() => import("./pages/Landing"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const YouTubeOAuthCallback = lazy(() => import("./pages/YouTubeOAuthCallback"));
const BuyACookie = lazy(() => import("./pages/BuyACookie"));
const CookieThanks = lazy(() => import("./pages/CookieThanks"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Trust = lazy(() => import("./pages/Trust"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Referrals = lazy(() => import("./pages/Referrals"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const Blog = lazy(() => import("./pages/Blog"));
const SavingsCalculator = lazy(() => import("./pages/SavingsCalculator"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient();

class AppErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="min-h-screen mesh-gradient flex items-center justify-center p-4 text-foreground">
        <div className="glass-panel rounded-2xl p-6 max-w-sm w-full text-center space-y-4">
          <h1 className="text-2xl font-bold tracking-tight">SaveySpend needs a refresh</h1>
          <p className="text-sm text-muted-foreground">A game or page failed to load correctly.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="glass-button h-11 w-full rounded-xl text-primary-foreground font-semibold"
          >
            Refresh app
          </button>
        </div>
      </main>
    );
  }
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ViewAsModBanner />
      <NoWifiDialog />
      <BrowserRouter>
        <DonationPopup />
        <ProUpgradePopup />
        <AppErrorBoundary>
          <Suspense fallback={<SaveyLoader />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/app" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/youtube-oauth" element={<YouTubeOAuthCallback />} />
              <Route path="/buy-a-cookie" element={<BuyACookie />} />
              <Route path="/cookie-thanks" element={<CookieThanks />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/trust" element={<Trust />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/r/:code" element={<ReferralRedirect />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/savings-calculator" element={<SavingsCalculator />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
               <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AppErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
